import { Order, orderStatusLabels, paymentMethodLabels, paymentStatusLabels } from '@/services/orders';
import { withImageFallback } from '@/utils/images';

const statusBadgeClass: Record<string, string> = {
  Pending: 'bg-[#FAF3E4] text-[#B5791F]',
  Processing: 'bg-[#E9F1FB] text-[#2A5CA8]',
  Shipped: 'bg-[#EFF7F1] text-[#1B3D2A]',
  Completed: 'bg-[#1B3D2A]/10 text-[#1B3D2A]',
  Cancelled: 'bg-[#fdebea] text-[#c3423f]',
};

const orderStatusSteps: { key: Order['OrderStatus']; label: string; icon: string }[] = [
  { key: 'Pending', label: '待處理', icon: 'ri-file-list-3-line' },
  { key: 'Processing', label: '處理中', icon: 'ri-loader-2-line' },
  { key: 'Shipped', label: '已出貨', icon: 'ri-truck-line' },
  { key: 'Completed', label: '已完成', icon: 'ri-checkbox-circle-line' },
];

export default function OrderDetailCard({ order }: { order: Order }) {
  const stepIndex = orderStatusSteps.findIndex((step) => step.key === order.OrderStatus);
  const isCancelled = order.OrderStatus === 'Cancelled';

  return (
    <div className="grid gap-6">
      <div className="rounded-2xl border border-[#E8E0D6] bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A574]">Order Number</p>
            <h2 className="text-xl font-bold text-[#1B3D2A]">{order.OrderNumber}</h2>
            <p className="mt-1 text-xs text-[#9A8A7A]">訂購時間：{order.CreatedAt}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-3 py-1.5 text-sm font-bold ${statusBadgeClass[order.OrderStatus] || ''}`}>
              {orderStatusLabels[order.OrderStatus]}
            </span>
            <span className="rounded-full bg-[#FAF8F5] px-3 py-1.5 text-sm font-bold text-[#5A6B5A]">
              {paymentStatusLabels[order.PaymentStatus]}
            </span>
          </div>
        </div>

        {!isCancelled && (
          <div className="mt-6 flex items-center gap-1 overflow-x-auto pb-1">
            {orderStatusSteps.map((step, index) => (
              <div key={step.key} className="flex flex-1 items-center gap-1 last:flex-none">
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm ${
                      index <= stepIndex ? 'bg-[#1B3D2A] text-white' : 'bg-[#FAF8F5] text-[#C9BFAF]'
                    }`}
                  >
                    <i className={step.icon} />
                  </div>
                  <span className={`text-[11px] font-medium whitespace-nowrap ${index <= stepIndex ? 'text-[#1B3D2A]' : 'text-[#C9BFAF]'}`}>
                    {step.label}
                  </span>
                </div>
                {index < orderStatusSteps.length - 1 && (
                  <div className={`h-0.5 flex-1 ${index < stepIndex ? 'bg-[#1B3D2A]' : 'bg-[#EFE8DE]'}`} />
                )}
              </div>
            ))}
          </div>
        )}
        {isCancelled && (
          <div className="mt-4 rounded-xl bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#c3423f]">此訂單已取消。</div>
        )}
      </div>

      <div className="rounded-2xl border border-[#E8E0D6] bg-white p-6">
        <h3 className="mb-4 text-base font-bold text-[#1B3D2A]">商品明細</h3>
        <div className="divide-y divide-[#E8E0D6]">
          {order.Items.map((item, index) => (
            <div key={`${item.ProductId}-${item.VariantId ?? index}`} className="flex items-center gap-4 py-4 first:pt-0 last:pb-0">
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-[#FAF8F5]">
                <img src={withImageFallback(item.ImageUrl)} alt={item.ProductName} className="h-full w-full object-cover object-top" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold text-[#1B3D2A]">{item.ProductName}</p>
                {item.VariantLabel && <p className="mt-0.5 text-xs text-[#9A8A7A]">規格：{item.VariantLabel}</p>}
                <p className="mt-0.5 text-xs text-[#9A8A7A]">SKU：{item.Sku || '—'}</p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm text-[#5A6B5A]">NT$ {item.UnitPrice.toLocaleString()} × {item.Quantity}</p>
                <p className="font-bold text-[#1B3D2A]">NT$ {item.LineTotal.toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-1.5 border-t border-[#E8E0D6] pt-4 text-sm">
          <div className="flex items-center justify-between text-[#5A6B5A]">
            <span>商品小計</span>
            <span>NT$ {order.SubtotalAmount.toLocaleString()}</span>
          </div>
          {order.DiscountAmount > 0 && (
            <div className="flex items-center justify-between text-[#5A6B5A]">
              <span>優惠折扣{order.ShippingCouponCode && <span className="ml-1 text-xs text-[#B5791F]">（{order.ShippingCouponCode}）</span>}</span>
              <span>-NT$ {order.DiscountAmount.toLocaleString()}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-[#5A6B5A]">
            <span>運費{order.ShippingMethodName ? `（${order.ShippingMethodName}）` : ''}</span>
            <span>
              {order.ShippingFee > 0 ? `NT$ ${order.ShippingFee.toLocaleString()}` : '免運費'}
              {order.ShippingCouponCode && order.DiscountAmount === 0 && <span className="ml-1 text-xs text-[#B5791F]">已套用優惠碼 {order.ShippingCouponCode}</span>}
            </span>
          </div>
          {order.PaymentExtraFee > 0 && (
            <div className="flex items-center justify-between text-[#5A6B5A]">
              <span>付款手續費</span>
              <span>NT$ {order.PaymentExtraFee.toLocaleString()}</span>
            </div>
          )}
          <div className="flex items-center justify-between pt-1.5 text-base font-bold text-[#1B3D2A]">
            <span>訂單總計</span>
            <span>NT$ {order.TotalAmount.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-[#E8E0D6] bg-white p-6">
          <h3 className="mb-3 text-base font-bold text-[#1B3D2A]">收件資訊</h3>
          <div className="space-y-1.5 text-sm text-[#5A6B5A]">
            <p>{order.RecipientName}</p>
            <p>{order.RecipientPhone}</p>
            <p>{order.RecipientEmail}</p>
            {order.PickupLocationName || order.PickupLocationAddress ? (
              <>
                <p className="font-bold text-[#1B3D2A]">取貨資訊</p>
                {order.PickupLocationName && <p>{order.PickupLocationName}</p>}
                {order.PickupLocationAddress && <p>{order.PickupLocationAddress}</p>}
                {order.PickupLocationPhone && <p>{order.PickupLocationPhone}</p>}
              </>
            ) : (
              <p>{order.ShippingPostalCode ? `${order.ShippingPostalCode} ` : ''}{order.ShippingCity}{order.ShippingDistrict}{order.ShippingAddressLine}</p>
            )}
            {order.Notes && <p className="text-[#9A8A7A]">備註：{order.Notes}</p>}
          </div>
        </div>
        <div className="rounded-2xl border border-[#E8E0D6] bg-white p-6">
          <h3 className="mb-3 text-base font-bold text-[#1B3D2A]">付款資訊</h3>
          <div className="space-y-1.5 text-sm text-[#5A6B5A]">
            <p>付款方式：{order.PaymentMethodName || paymentMethodLabels[order.PaymentMethod] || order.PaymentMethod}</p>
            <p>付款狀態：{paymentStatusLabels[order.PaymentStatus]}</p>
            {order.PaymentStatus === 'Unpaid' && order.PaymentDueAt && (
              <p className="mt-2 rounded-xl bg-[#FAF8F5] px-3 py-2 text-xs leading-5 text-[#9A8A7A]">
                請於 <strong className="text-[#1B3D2A]">{order.PaymentDueAt}</strong> 前完成付款，逾期系統將自動取消訂單。
              </p>
            )}
            {order.PaymentStatus === 'Unpaid' && !order.PaymentDueAt && order.PaymentMethod === 'COD' && (
              <p className="mt-2 rounded-xl bg-[#FAF8F5] px-3 py-2 text-xs leading-5 text-[#9A8A7A]">
                商品出貨後由物流人員向您收取貨款，請保持電話暢通。
              </p>
            )}
            {order.PaymentStatus === 'Unpaid' && !order.PaymentDueAt && order.PaymentMethod !== 'COD' && !order.PaymentInstructions && (
              <p className="mt-2 rounded-xl bg-[#FAF8F5] px-3 py-2 text-xs leading-5 text-[#9A8A7A]">
                請完成線上付款程序，付款成功後系統將自動更新此訂單的付款狀態。
              </p>
            )}
            {/* 銀行轉帳等人工結算付款方式的匯款資訊：後台在「付款方式設定」填寫的付款說明，下單當下已快照到
                這筆訂單，只要還在待付款狀態就直接顯示給客人，不用等客服另外聯繫。 */}
            {order.PaymentStatus === 'Unpaid' && order.PaymentInstructions && (
              <div className="mt-2 whitespace-pre-wrap rounded-xl bg-[#FAF8F5] px-3 py-2 text-xs leading-5 text-[#9A8A7A]">
                {order.PaymentInstructions}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
